import { Module } from '../../api/module';
import useLicenses from '../../hooks/useLicenses';
import ModuleLicenseAlert from './ModuleLicenseAlert';
import { t } from 'i18next';
import { getRoleTranslationKey } from '../../pages/material/toc/components/utils';
import Alert from '@pelckmans/elementis/components/alert';
import { useContext } from 'react';
import IdentityContext from '../../contexts/IdentityContext';
import AlertGroup from '@pelckmans/elementis/components/alert-group';
import Role from '../../core/security/role';

const TRANSLATION_KEY = 'SHARED_COMPONENTS.ALERTS.AGGREGATED.';

type AlertType =
  | 'STUDENT_NO_DOCUMENT'
  | 'TEACHER_NO_DOCUMENT'
  | 'TEACHER_NO_DIGITAL_MANUAL'
  | 'PREVIEW'
  | 'REVIEW'
  | 'PHASEDOUT'
  | 'INCOMPLETE';

interface Props {
  selectedModules: Module[];
  children?: JSX.Element;
  showLicenseAlertsOnly?: boolean;
}

export default function AggregatedModuleAlerts({ selectedModules, children, showLicenseAlertsOnly = false }: Props) {
  const { identity } = useContext(IdentityContext);
  const { getUserLicenseValidationType } = useLicenses();

  const alertTypes = selectedModules.reduce((acc, module) => {
    const type = getUserLicenseValidationType(module);
    if (type !== 'LICENSED') {
      acc[type] = true;
    }

    if (showLicenseAlertsOnly) {
      return acc;
    }

    if (['preview', 'review', 'phasedOut'].includes(module.publishingState)) {
      acc[module.publishingState.toUpperCase() as AlertType] = true;
    }

    if (module.incomplete) {
      acc['INCOMPLETE'] = true;
    }

    return acc;
  }, {} as Record<AlertType, number | boolean>);

  if (!children && Object.keys(alertTypes).length === 0) {
    return null;
  }

  const alerts = Object.entries(alertTypes).map(([type, data]) => {
    const translationKey = `${TRANSLATION_KEY}${type}`;
    switch (type) {
      case 'STUDENT_NO_DOCUMENT':
      case 'TEACHER_NO_DOCUMENT':
      case 'TEACHER_NO_DIGITAL_MANUAL':
        return <ModuleLicenseAlert key={type} message={t(translationKey)} />;
      case 'PREVIEW': {
        const hasExtraAccess = identity.hasAnyRole([
          Role.SYSADMIN,
          Role.PRODUCTION,
          Role.EDITOR,
          Role.PUBLISHER,
          Role.SALES,
        ]);
        const role = hasExtraAccess ? t(getRoleTranslationKey(identity.highestRole, identity.isAuthor)) : undefined;
        return (
          <Alert key={type} prominence="info">
            {t(`${translationKey}${hasExtraAccess ? '_EXTRA_ACCESS' : ''}`, { role })}
          </Alert>
        );
      }
      case 'REVIEW': {
        const role = t(getRoleTranslationKey(identity.highestRole, identity.isAuthor));
        return (
          <Alert key={type} prominence="info">
            {t(translationKey, { role })}
          </Alert>
        );
      }
      case 'PHASEDOUT': {
        if (identity.isTeacherAlike) {
          return (
            <Alert key={type} prominence="info">
              {t(translationKey)}
            </Alert>
          );
        }
        return (
          <Alert key={type} prominence="info">
            {t(`${translationKey}_STUDENT`)}
          </Alert>
        );
      }
      case 'INCOMPLETE':
        return (
          <Alert key={type} prominence="info">
            {t(translationKey)}
          </Alert>
        );
    }

    return null;
  });

  return !children && alerts.length === 1 ? (
    alerts
  ) : (
    <div data-testid="aggregated-module-alerts-group">
      <AlertGroup aria-label={undefined} role="group">
        {alerts}
        {children}
      </AlertGroup>
    </div>
  );
}
